import React, { useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { useDispatch } from "react-redux"
import { useSelector } from "react-redux"
import { Button, ModalFooter } from "reactstrap"
import { updateProfileDocument } from "store/actions/authAction"
import { showAlert } from "store/reducers/alertReducer"

const UploadDocument = ({
  userDetails,
  uploadDocType,
  toggleModal,
  getDetails,
}) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { isLoading } = useSelector(state => state.Login)
  const [file, setFile] = useState(null)
  const fileInputRef = useRef(null)

  const compressImage = (file, maxSize) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.readAsDataURL(file)

      reader.onload = event => {
        const img = new Image()
        img.src = event.target.result

        img.onload = () => {
          const canvas = document.createElement("canvas")
          const ctx = canvas.getContext("2d")

          let width = img.width
          let height = img.height

          // Resize if needed (reduce dimensions to save size)
          const maxWidth = 1024 // Adjust for your needs
          const maxHeight = 1024
          if (width > maxWidth || height > maxHeight) {
            if (width > height) {
              height *= maxWidth / width
              width = maxWidth
            } else {
              width *= maxHeight / height
              height = maxHeight
            }
          }

          canvas.width = width
          canvas.height = height
          ctx.drawImage(img, 0, 0, width, height)

          let quality = 0.9 // Start with high quality
          let compressedDataUrl = canvas.toDataURL("image/jpeg", quality)

          while (compressedDataUrl.length > maxSize && quality > 0.1) {
            quality -= 0.1
            compressedDataUrl = canvas.toDataURL("image/jpeg", quality)
          }

          fetch(compressedDataUrl)
            .then(res => res.blob())
            .then(blob => {
              const compressedFile = new File([blob], file.name, {
                type: "image/jpeg",
              })
              resolve(compressedFile)
            })
            .catch(reject)
        }
      }

      reader.onerror = error => reject(error)
    })
  }

  const handleFileChange = async event => {
    let selectedFile = event.target.files[0]
    if (!selectedFile) return

    const maxSize = 2 * 1024 * 1024 // 5MB
    const allowedImageTypes = ["image/png", "image/jpeg", "image/jpg"]
    const allowedCvTypes = ["application/pdf"]

    if (selectedFile.size > maxSize) {
      selectedFile = await compressImage(selectedFile, maxSize)
    }

    if (
      (uploadDocType === "cv_attachment" &&
        !allowedCvTypes.includes(selectedFile.type)) ||
      (["profile_picture", "national_id_image", "supporting_document"].includes(
        uploadDocType
      ) &&
        !allowedImageTypes.includes(selectedFile.type))
    ) {
      dispatch(showAlert({ text: t("Invalid file type"), type: "error" }))
      return
    }

    setFile(selectedFile)
  }

  const handleUpdate = () => {
    if (file) {
      let formData = new FormData()
      formData.append("type", uploadDocType)
      formData.append("file", file)

      const data = {
        uuid: userDetails?.user_information?.uuid,
        payload: formData,
      }

      dispatch(updateProfileDocument(data))
        .then(action => {
          const { statusCode, success, message, data } = action?.payload
          if (statusCode === 200 && success) {
            dispatch(showAlert({ text: message, type: "success" }))
            getDetails()
            toggleModal()
          } else {
            dispatch(showAlert({ text: message, type: "error" }))
          }
        })
        .catch(error => {})
    }
  }

  return (
    <div className="row">
      <div className="col-lg-12">
        <div className="form_field">
          {/* Display accepted file types in a paragraph */}
          <input
            type="file"
            className="p-1"
            accept={
              uploadDocType === "cv_attachment"
                ? ".pdf"
                : "image/png, image/jpeg, image/jpg"
            }
            onChange={handleFileChange}
            ref={fileInputRef}
          />
          <p className="text-muted mt-2">
            {t("Accepted file types")}:{" "}
            {uploadDocType === "cv_attachment" ? "PDF" : "PNG, JPEG, JPG"}
          </p>
        </div>
      </div>
      <div className="col-lg-12">
        <ModalFooter>
          <Button color="secondary" type="button" onClick={toggleModal}>
            {t("Close")}
          </Button>
          <Button
            color="success"
            type="submit"
            onClick={handleUpdate}
            disabled={isLoading || !file}
          >
            {t("Update")}
          </Button>
        </ModalFooter>
      </div>
    </div>
  )
}

export default UploadDocument
